// v0.8 trades between real players: offers for you, your offers, and recent deals.
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, ago, duration } from '../state.tsx';
import { Loading, Panel, useLoad, PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { TradeSide, ProposeTrade } from '../components/v08.tsx';
import { sequence, play } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

export function TradesTab() {
  const { me, act } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/trades'), [me?.pendingTrades, me?.cash], 20_000);
  const people = useLoad(() => api.get('/api/chat/people'), []);
  const [with_, setWith] = useState<{ id: number; name: string } | null>(null);
  if (!data || !people.data) return <Loading />;
  const answer = async (t: any, accept: boolean) => {
    const r = await act('/api/trades/answer', { tradeId: t.id, accept });
    if (!r) return;
    if (accept) { sequence([[0, 'handshake'], [500, 'medal']]); fx.stamp('Deal', `with ${t.other.name}`, 'good'); fx.coins(12); } else play('flip');
    reload();
  };
  const cancel = async (t: any) => {
    if (await act('/api/trades/cancel', { tradeId: t.id })) { play('flip'); reload(); }
  };
  const incoming = data.trades.filter((t: any) => t.status === 'pending' && !t.mine);
  const outgoing = data.trades.filter((t: any) => t.status === 'pending' && t.mine);
  const done = data.trades.filter((t: any) => t.status !== 'pending');
  const others = people.data.players.filter((p: any) => p.id !== me.id);
  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        Swap items and cash with other real players. Whoever makes the offer has their side held by the game until the other player says yes
        (both sides move at once) or no. You can also send cash or items as a gift from anyone’s profile.
      </p>
      {incoming.length > 0 && (
        <Panel title="Offers for you">
          {incoming.map((t: any) => (
            <div key={t.id} className="trade-card incoming">
              <Portrait seed={t.other.portrait} playerId={t.other.id} size={40} />
              <div className="grow">
                <div><PlayerLink id={t.other.id} name={t.other.name} /> offers <TradeSide items={t.giveItems} cash={t.giveCash} /></div>
                <div>for your <TradeSide items={t.wantItems} cash={t.wantCash} /></div>
                {t.note && <div className="small">“{t.note}”</div>}
                <div className="small dim">Expires in {duration(Math.max(0, t.expiresAt - Date.now()))}</div>
              </div>
              <div className="bet-actions">
                <button className="btn green" onClick={() => answer(t, true)}>Accept</button>
                <a className="small" onClick={() => answer(t, false)}>Decline</a>
              </div>
            </div>
          ))}
        </Panel>
      )}
      <Panel title="Make an offer">
        {others.length === 0 ? <div className="muted">There are no other real players yet.</div> : (
          <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="trade-with">Trade with</label>
            <select id="trade-with" value={with_?.id ?? ''} onChange={(e) => { const p = others.find((x: any) => x.id === Number(e.target.value)); setWith(p ? { id: p.id, name: p.name } : null); }}>
              <option value="">pick a player…</option>
              {others.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <span className="small dim">then choose what you give and what you want.</span>
          </div>
        )}
      </Panel>
      {with_ && <ProposeTradeOnce to={with_} onClose={() => setWith(null)} onDone={reload} />}
      {outgoing.length > 0 && (
        <Panel title="Your offers">
          {outgoing.map((t: any) => (
            <div key={t.id} className="feed-item" style={{ alignItems: 'center' }}>
              <span className="grow">To <PlayerLink id={t.other.id} name={t.other.name} />: <TradeSide items={t.giveItems} cash={t.giveCash} /> for <TradeSide items={t.wantItems} cash={t.wantCash} />
                <span className="small dim"> · expires in {duration(Math.max(0, t.expiresAt - Date.now()))}</span></span>
              <a className="small" onClick={() => cancel(t)}>take it back</a>
            </div>
          ))}
        </Panel>
      )}
      {done.length > 0 && (
        <Panel title="This week">
          {done.map((t: any) => (
            <div key={t.id} className="feed-item">
              <span className="grow"><span className={`sb-stamp ${t.status === 'accepted' ? 'good' : 'dim'}`}>{t.status === 'accepted' ? 'Done' : t.status === 'declined' ? 'Declined' : t.status === 'expired' ? 'Expired' : 'Withdrawn'}</span>{' '}
                {t.mine ? <>You → <PlayerLink id={t.other.id} name={t.other.name} /></> : <><PlayerLink id={t.other.id} name={t.other.name} /> → you</>}: <TradeSide items={t.giveItems} cash={t.giveCash} /> for <TradeSide items={t.wantItems} cash={t.wantCash} /></span>
              <span className="when">{ago(t.resolvedAt ?? t.createdAt)}</span>
            </div>
          ))}
        </Panel>
      )}
      <div className="small dim">Offers last {data.rules.expireHours} hours. Gifts and trades have no limits, so only trade with people you trust.</div>
    </div>
  );
}

function ProposeTradeOnce(props: { to: { id: number; name: string }; onClose: () => void; onDone: () => void }) {
  return <ProposeTrade key={props.to.id} {...props} />;
}
